import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { VwDatePipe } from '../../pipes/vw-date.pipe';
import { VwNumberPipe } from '../../pipes/vw-number.pipe';

/** Degré entier, sans « -0 » pour une valeur entre -0,5 et 0. */
export function roundDegrees(value: number): number {
  return Math.round(value) || 0;
}

/** Une journée en mini : le jour, son pictogramme, la température maximale. */
export interface ForecastMiniDay {
  /** Le jour (« 2026-11-04 ») : il s'écrit court dans la langue de l'app (« mer. »). */
  date: string | Date;
  /** Adresse du pictogramme météo (`/assets/svg/weather/…`). */
  icon: string;
  /** Maximum du jour en °C, arrondi à l'affichage ; absent, rien ne s'écrit. */
  max: number | null;
}

/**
 * Les jours qui viennent en mini (`vw-forecast-mini`) : pour chacun le jour
 * court, le pictogramme et le maximum, en 12 px. Répond d'un coup d'œil à « je
 * peux semer ce week-end ? » ; le détail reste sur la page Météo.
 *
 * Rien d'interactif : il se pose dans une ligne qui ouvre la page Météo
 * (`vwListRowTrailing` d'une `vw-list-row`), et le lecteur d'écran lit les
 * jours avec le reste de la ligne. Avec le texte agrandi, les colonnes se
 * resserrent au lieu de couper.
 * Usage : `<vw-forecast-mini [days]="next3" />`
 */
@Component({
  selector: 'vw-forecast-mini',
  standalone: true,
  imports: [VwDatePipe, VwNumberPipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`:host { display: block; }`],
  template: `
    <span class="flex gap-0.5" data-test="forecast-mini">
      @for (day of days; track $index) {
        <span class="flex min-w-9 flex-col items-center text-caption leading-tight">
          <span class="font-normal text-vw-muted">{{ day.date | vwDate: 'weekday-short' }}</span>
          <img [src]="day.icon" alt="" class="h-[18px] w-[18px]" />
          <span class="font-semibold text-vw-ink">{{ day.max === null ? '' : (round(day.max) | vwNumber: 0) + '°' }}</span>
        </span>
      }
    </span>
  `,
})
export class ForecastMiniComponent {
  /** Les jours à montrer, dans l'ordre (trois sur l'accueil). */
  @Input() days: readonly ForecastMiniDay[] = [];

  readonly round = roundDegrees;
}
